import React, { useState } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Microscope,
  Search,
  Filter,
  Download,
  Plus,
  Eye,
  FileText,
  CheckCircle,
  Clock,
  XCircle,
  Upload,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

export default function LabCoaManagementPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCoa, setSelectedCoa] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Mock COA data
  const coas = [
    {
      id: 1,
      coaNumber: 'COA-2024-1234',
      batchNumber: 'BT-45891',
      productName: 'Blue Dream',
      labName: 'CannaTest Analytics',
      testDate: '2024-11-28',
      receiveDate: '2024-11-30',
      status: 'Passed',
      thcContent: '22.5%',
      cbdContent: '0.8%',
      terpenes: 'Myrcene, Pinene, Caryophyllene',
      microbials: 'Pass',
      pesticides: 'Pass',
      heavyMetals: 'Pass',
      residualSolvents: 'Pass',
      moisture: '11.2%',
      fileUrl: '/documents/coa-2024-1234.pdf'
    },
    {
      id: 2,
      coaNumber: 'COA-2024-1235',
      batchNumber: 'BT-45823',
      productName: 'Sour Diesel Gummies',
      labName: 'Quality Labs Inc',
      testDate: '2024-12-01',
      receiveDate: null,
      status: 'Pending',
      thcContent: '10mg per unit',
      cbdContent: '0mg',
      terpenes: 'N/A',
      microbials: 'Pending',
      pesticides: 'Pending',
      heavyMetals: 'Pending',
      residualSolvents: 'Pending',
      moisture: 'N/A',
      fileUrl: null
    },
    {
      id: 3,
      coaNumber: 'COA-2024-1236',
      batchNumber: 'BT-45734',
      productName: 'OG Kush Concentrate',
      labName: 'CannaTest Analytics',
      testDate: '2024-11-25',
      receiveDate: '2024-11-27',
      status: 'Passed',
      thcContent: '85.3%',
      cbdContent: '1.2%',
      terpenes: 'Limonene, Linalool, Humulene',
      microbials: 'Pass',
      pesticides: 'Pass',
      heavyMetals: 'Pass',
      residualSolvents: 'Pass',
      moisture: 'N/A',
      fileUrl: '/documents/coa-2024-1236.pdf'
    },
    {
      id: 4,
      coaNumber: 'COA-2024-1237',
      batchNumber: 'BT-45912',
      productName: 'Lemon Haze Pre-Rolls',
      labName: 'Quality Labs Inc',
      testDate: '2024-11-30',
      receiveDate: '2024-12-02',
      status: 'Failed',
      thcContent: '19.8%',
      cbdContent: '0.5%',
      terpenes: 'Limonene, Terpinolene',
      microbials: 'Fail',
      pesticides: 'Pass',
      heavyMetals: 'Pass',
      residualSolvents: 'Pass',
      moisture: '15.8%',
      fileUrl: '/documents/coa-2024-1237.pdf',
      failureReason: 'Microbial contamination detected - E. coli present'
    },
    {
      id: 5,
      coaNumber: 'COA-2024-1238',
      batchNumber: 'BT-45689',
      productName: 'CBD Tincture',
      labName: 'CannaTest Analytics',
      testDate: '2024-11-20',
      receiveDate: '2024-11-22',
      status: 'Passed',
      thcContent: '0.3%',
      cbdContent: '1000mg per bottle',
      terpenes: 'N/A',
      microbials: 'Pass',
      pesticides: 'Pass',
      heavyMetals: 'Pass',
      residualSolvents: 'Pass',
      moisture: 'N/A',
      fileUrl: '/documents/coa-2024-1238.pdf'
    },
    {
      id: 6,
      coaNumber: 'COA-2024-1239',
      batchNumber: 'BT-45801',
      productName: 'Purple Kush',
      labName: 'Premium Testing Labs',
      testDate: '2024-11-26',
      receiveDate: '2024-11-28',
      status: 'Passed',
      thcContent: '24.1%',
      cbdContent: '0.6%',
      terpenes: 'Myrcene, Pinene, Limonene',
      microbials: 'Pass',
      pesticides: 'Pass',
      heavyMetals: 'Pass',
      residualSolvents: 'Pass',
      moisture: '10.5%',
      fileUrl: '/documents/coa-2024-1239.pdf'
    },
    {
      id: 7,
      coaNumber: 'COA-2024-1240',
      batchNumber: 'BT-45745',
      productName: 'Vape Cartridge - Hybrid',
      labName: 'CannaTest Analytics',
      testDate: '2024-12-03',
      receiveDate: null,
      status: 'In Progress',
      thcContent: '78.5%',
      cbdContent: '1.8%',
      terpenes: 'Testing',
      microbials: 'Testing',
      pesticides: 'Testing',
      heavyMetals: 'Testing',
      residualSolvents: 'Testing',
      moisture: 'N/A',
      fileUrl: null
    },
    {
      id: 8,
      coaNumber: 'COA-2024-1241',
      batchNumber: 'BT-45623',
      productName: 'Chocolate Bar - 100mg',
      labName: 'Quality Labs Inc',
      testDate: '2024-11-29',
      receiveDate: '2024-12-01',
      status: 'Passed',
      thcContent: '100mg per bar',
      cbdContent: '0mg',
      terpenes: 'N/A',
      microbials: 'Pass',
      pesticides: 'Pass',
      heavyMetals: 'Pass',
      residualSolvents: 'Pass',
      moisture: 'N/A',
      fileUrl: '/documents/coa-2024-1241.pdf'
    }
  ];

  const filteredCoas = coas.filter(coa => {
    const matchesSearch = 
      coa.coaNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coa.batchNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coa.productName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || coa.status.toLowerCase() === statusFilter.toLowerCase();
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Passed':
        return <Badge variant="success" className="flex items-center gap-1"><CheckCircle className="h-3 w-3" />Passed</Badge>;
      case 'Failed':
        return <Badge variant="destructive" className="flex items-center gap-1"><XCircle className="h-3 w-3" />Failed</Badge>;
      case 'Pending':
        return <Badge className="bg-yellow-500 text-white flex items-center gap-1"><Clock className="h-3 w-3" />Pending</Badge>;
      case 'In Progress':
        return <Badge className="bg-blue-500 text-white flex items-center gap-1"><Microscope className="h-3 w-3" />In Progress</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const summaryCards = [
    {
      title: 'Total COAs',
      value: coas.length,
      icon: FileText,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: 'Passed',
      value: coas.filter(c => c.status === 'Passed').length,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      title: 'In Testing',
      value: coas.filter(c => c.status === 'Pending' || c.status === 'In Progress').length,
      icon: Clock,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50'
    },
    {
      title: 'Failed',
      value: coas.filter(c => c.status === 'Failed').length,
      icon: XCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-50'
    },
  ];

  const viewCoaDetails = (coa) => {
    setSelectedCoa(coa);
    setIsDialogOpen(true);
  };

  const handleExport = () => {
    toast.success('Exporting COA data to Excel...');
  };

  const handleDownloadCoa = (coaNumber) => {
    toast.success(`Downloading COA ${coaNumber}...`);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Lab & COA Management</h1>
            <p className="text-muted-foreground mt-1">Test results and certificates of analysis</p>
          </div>
          <Button className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            Upload COA
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {summaryCards.map((card, index) => (
            <Card key={index}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{card.title}</p>
                    <p className="text-3xl font-bold mt-2">{card.value}</p>
                  </div>
                  <div className={`p-3 ${card.bgColor} rounded-lg`}>
                    <card.icon className={`h-6 w-6 ${card.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* COA Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>All Certificates of Analysis</CardTitle>
                <CardDescription>Lab test results and compliance certificates</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleExport}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className="flex items-center gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by COA number, batch, or product..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="passed">Passed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in progress">In Progress</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Table */}
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>COA Number</TableHead>
                    <TableHead>Batch Number</TableHead>
                    <TableHead>Product Name</TableHead>
                    <TableHead>Lab Name</TableHead>
                    <TableHead>Test Date</TableHead>
                    <TableHead>THC</TableHead>
                    <TableHead>Microbials</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCoas.length > 0 ? (
                    filteredCoas.map((coa) => (
                      <TableRow key={coa.id}>
                        <TableCell className="font-medium">{coa.coaNumber}</TableCell>
                        <TableCell>{coa.batchNumber}</TableCell>
                        <TableCell>{coa.productName}</TableCell>
                        <TableCell>{coa.labName}</TableCell>
                        <TableCell>{coa.testDate}</TableCell>
                        <TableCell>{coa.thcContent}</TableCell>
                        <TableCell>
                          {coa.microbials === 'Pass' && <Badge variant="success" className="text-xs">Pass</Badge>}
                          {coa.microbials === 'Fail' && <Badge variant="destructive" className="text-xs">Fail</Badge>}
                          {(coa.microbials === 'Pending' || coa.microbials === 'Testing') && <Badge className="text-xs">Testing</Badge>}
                        </TableCell>
                        <TableCell>{getStatusBadge(coa.status)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => viewCoaDetails(coa)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {coa.fileUrl && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadCoa(coa.coaNumber)}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-muted-foreground">
                        No COAs found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* COA Details Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Certificate of Analysis Details</DialogTitle>
            <DialogDescription>Complete test results and compliance information</DialogDescription>
          </DialogHeader>
          {selectedCoa && (
            <div className="space-y-6">
              {/* Header Info */}
              <div className="grid grid-cols-2 gap-4 pb-4 border-b">
                <div>
                  <p className="text-sm text-muted-foreground">COA Number</p>
                  <p className="font-medium">{selectedCoa.coaNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <div className="mt-1">{getStatusBadge(selectedCoa.status)}</div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Batch Number</p>
                  <p className="font-medium">{selectedCoa.batchNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Product Name</p>
                  <p className="font-medium">{selectedCoa.productName}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Lab Name</p>
                  <p className="font-medium">{selectedCoa.labName}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Test Date</p>
                  <p className="font-medium">{selectedCoa.testDate}</p>
                </div>
              </div>

              {/* Potency Results */}
              <div>
                <h3 className="font-semibold mb-3">Potency Analysis</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 border rounded-lg">
                    <p className="text-sm text-muted-foreground">THC Content</p>
                    <p className="text-lg font-bold text-primary">{selectedCoa.thcContent}</p>
                  </div>
                  <div className="p-3 border rounded-lg">
                    <p className="text-sm text-muted-foreground">CBD Content</p>
                    <p className="text-lg font-bold text-primary">{selectedCoa.cbdContent}</p>
                  </div>
                </div>
              </div>

              {/* Safety Tests */}
              <div>
                <h3 className="font-semibold mb-3">Safety Testing Results</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <span className="font-medium">Microbials</span>
                    {selectedCoa.microbials === 'Pass' && <Badge variant="success">Pass</Badge>}
                    {selectedCoa.microbials === 'Fail' && <Badge variant="destructive">Fail</Badge>}
                    {(selectedCoa.microbials === 'Pending' || selectedCoa.microbials === 'Testing') && <Badge>Testing</Badge>}
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <span className="font-medium">Pesticides</span>
                    {selectedCoa.pesticides === 'Pass' && <Badge variant="success">Pass</Badge>}
                    {selectedCoa.pesticides === 'Fail' && <Badge variant="destructive">Fail</Badge>}
                    {(selectedCoa.pesticides === 'Pending' || selectedCoa.pesticides === 'Testing') && <Badge>Testing</Badge>}
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <span className="font-medium">Heavy Metals</span>
                    {selectedCoa.heavyMetals === 'Pass' && <Badge variant="success">Pass</Badge>}
                    {selectedCoa.heavyMetals === 'Fail' && <Badge variant="destructive">Fail</Badge>}
                    {(selectedCoa.heavyMetals === 'Pending' || selectedCoa.heavyMetals === 'Testing') && <Badge>Testing</Badge>}
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <span className="font-medium">Residual Solvents</span>
                    {selectedCoa.residualSolvents === 'Pass' && <Badge variant="success">Pass</Badge>}
                    {selectedCoa.residualSolvents === 'Fail' && <Badge variant="destructive">Fail</Badge>}
                    {(selectedCoa.residualSolvents === 'Pending' || selectedCoa.residualSolvents === 'Testing') && <Badge>Testing</Badge>}
                  </div>
                </div>
              </div>

              {/* Terpenes */}
              <div>
                <h3 className="font-semibold mb-2">Terpene Profile</h3>
                <p className="text-sm p-3 bg-muted rounded-lg">{selectedCoa.terpenes}</p>
              </div>

              {/* Failure Reason if applicable */}
              {selectedCoa.failureReason && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                    <div>
                      <p className="font-semibold text-red-900">Test Failure Reason</p>
                      <p className="text-sm text-red-700 mt-1">{selectedCoa.failureReason}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                {selectedCoa.fileUrl && (
                  <Button className="flex-1" onClick={() => handleDownloadCoa(selectedCoa.coaNumber)}>
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                )}
                <Button variant="outline" className="flex-1">Request Retest</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
